import { isInIframe } from '../utils/iframe';
import { Integration } from './Integration';
import { IntegrationLogger } from './IntegrationLogger';
import {
  ConfigSessionStorageService,
  ConfigMemoryStorageService,
} from './services/ConfigStorageService';
import { TokenService } from './services/TokenService';
import { WindowMessagingService } from './services/WindowMessagingService';
import type { ICrossWindowLogEntry } from './shared/crossWindowMessaging';
import { ELogCrossWindowEvents } from './shared/crossWindowMessaging';
import type { TIntegrationConfigUnionPartial } from './shared/integration';

export type TIntegrationOptions = {
  // TODO remove it? double-check with the live team
  // Whitelist of origins who can open an integration,
  // if document referrer origin is not in the list -
  // SDK won't be created and an error will be thrown.
  // If not provided - wildcard (*) is used.
  allowedOrigins?: Array<string>;
};

export class IntegrationFactory {
  public static create<C extends TIntegrationConfigUnionPartial = TIntegrationConfigUnionPartial>(
    integrationOptions: TIntegrationOptions = {},
  ): Integration<C> {
    const windowMessenger = new WindowMessagingService({
      // TODO remove this behaviour and always rely on document.referrer?
      // There is an HTTP header called Referrer-Policy to control how much referrer information is sent.
      // If our customer's website will have Referrer-Policy: 'no-referrer' or Referrer-Policy: 'same-origin'
      // the document.referrer will be an empty string, so we fall back to wildcard.
      // Wildcard is not safe, but document.referrer could be anyone, so it shouldn't matter.
      //
      // To be truly safe, we should ask clients to change Referrer-Policy (so we always have a real origin and remove wildcard fallback)
      // and we also should always set allowedOrigins.
      targetOrigin: document.referrer
        ? new URL(document.referrer).origin
        : WindowMessagingService.WILDCARD_ORIGIN,
      allowedOrigins: integrationOptions.allowedOrigins ?? [WindowMessagingService.WILDCARD_ORIGIN],
    });
    IntegrationLogger.setCrossWindowLogTransport((logEntry: ICrossWindowLogEntry) => {
      void windowMessenger.sendMessage(
        ELogCrossWindowEvents.SEND_LOG_FROM_INTEGRATION_TO_HOST,
        logEntry,
      );
    });
    return new Integration<C>(
      windowMessenger,
      isInIframe() ? new ConfigMemoryStorageService<C>() : new ConfigSessionStorageService<C>(),
      new TokenService(),
    );
  }
}
